/**
 * Router-state helpers.
 *
 * Kept out of `AppShell.tsx` so that file exports only components — a module that
 * mixes both defeats React Fast Refresh, which then does a full reload on every edit
 * and loses the workspace state an operator was inspecting.
 */

/**
 * Narrows the `from` value carried in router state without trusting it.
 *
 * An attempted path is only honoured when it is a plausible in-app absolute path;
 * anything else (missing, non-string, protocol-relative, the login page itself) is
 * rejected so sign-in cannot be turned into an open redirect or a loop.
 */
export function intendedPathFromState(state: unknown): string | null {
  if (typeof state !== 'object' || state === null) return null;
  const from = (state as Record<string, unknown>)['from'];
  if (typeof from !== 'string' || from === '' || !from.startsWith('/')) return null;
  // `//evil.example.com` is a protocol-relative URL, not an in-app path.
  if (from.startsWith('//')) return null;
  // Never bounce back to the login page itself.
  if (from.startsWith('/login')) return null;
  return from;
}

/**
 * Carries a sign-out failure from the shell to the sign-in screen.
 *
 * The shell unmounts when it navigates away, so a component-local message would be
 * lost exactly when it matters — the one case where the operator must be told the
 * server session may still be alive.
 */
export function signOutNoticeFromState(state: unknown): string | null {
  if (typeof state !== 'object' || state === null) return null;
  const notice = (state as Record<string, unknown>)['signOutNotice'];
  return typeof notice === 'string' && notice !== '' ? notice : null;
}
